/**
 * Seed data (design §14): 14 procedures across 2 years
 * (2024: 5 · 2025: 9), 2 with complications, 3 with a generated summary.
 * Loaded only at the user's request ("Explore with sample data").
 * Record ids are stable so the seed never loads twice.
 */
import i18n from '@/i18n';
import type { Surgery } from './types';
import { generateFicha } from './fichaEngine';

function entry(
  seq: number,
  year: number,
  date: string,
  c: Omit<Surgery, 'id' | 'folio' | 'fecha' | 'creadoEn' | 'actualizadoEn'>,
): Surgery {
  const folio = `SUT-${year}-${String(seq).padStart(4, '0')}`;
  const creadoEn = `${date}T18:30:00.000Z`;
  return {
    id: `semilla-${year}-${String(seq).padStart(4, '0')}`,
    folio,
    fecha: `${date}T09:00:00.000Z`,
    ...c,
    creadoEn,
    actualizadoEn: creadoEn,
    demo: true, // ephemeral sample data: never synced, purged on login (persistence.ts)
  };
}

export const SEED: Surgery[] = [
  // ───────────────────────── 2024 (5) ─────────────────────────
  entry(1, 2024, '2024-02-12', {
    paciente: { iniciales: 'M.G.R.', edad: 54, sexo: 'F' },
    diagnostico: 'Acute calculous cholecystitis',
    categoria: 'Laparoscopic cholecystectomy',
    tecnica: 'Laparoscopic',
    rol: 'Lead surgeon',
    duracionMin: 75,
    descripcion:
      'Pneumoperitoneum with a Veress needle and placement of four trocars in the usual arrangement. Dissection of Calot’s triangle with a Maryland dissector until the critical view of safety was achieved. Clipping and division of the cystic artery and duct. Extraction of the gallbladder in an endobag through the umbilical port. Hemostasis of the gallbladder bed and closure of the 10 mm port fascia.',
    materiales: ['10 mm trocar', '5 mm trocar', 'Endobag', 'Titanium clips', '2-0 suture'],
    complicaciones: { hubo: false },
    lecciones:
      'The critical view of safety is non-negotiable: if it is not clearly seen, dissect further or convert.',
  }),
  entry(2, 2024, '2024-04-03', {
    paciente: { iniciales: 'J.A.P.', edad: 31, sexo: 'M' },
    diagnostico: 'Acute gangrenous appendicitis',
    categoria: 'Laparoscopic appendectomy',
    tecnica: 'Laparoscopic',
    rol: 'Lead surgeon',
    duracionMin: 65,
    descripcion:
      'Access with the Hasson technique. Gangrenous appendix with a friable base at risk of tearing. Division of the mesoappendix with a harmonic scalpel. Ligation of the base with two endoloops and a generous distal division. Irrigation of the cavity and aspiration of the collection in the iliac fossa. Layered closure.',
    materiales: ['Harmonic scalpel', 'Endoloop', '12 mm trocar'],
    complicaciones: {
      hubo: true,
      grado: 'I',
      descripcion: 'Seroma at the umbilical port site, resolved with local wound care.',
    },
    lecciones:
      'In friable bases, double ligation and a generous distal division prevent stump leaks.',
  }),
  entry(3, 2024, '2024-06-21', {
    paciente: { iniciales: 'R.T.S.', edad: 62, sexo: 'M' },
    diagnostico: 'Indirect right inguinal hernia',
    categoria: 'Inguinal hernioplasty',
    tecnica: 'Open',
    rol: 'Lead surgeon',
    duracionMin: 55,
    descripcion:
      'Oblique incision over the inguinal canal. Opening of the external oblique aponeurosis. Dissection of the cord and reduction of the indirect sac. Placement of a polypropylene mesh fixed with a continuous 3-0 suture to the inguinal ligament and the pubic tubercle, Lichtenstein technique. Layered closure.',
    materiales: ['Polypropylene mesh 15×7.5', '3-0 suture', '4-0 suture'],
    complicaciones: { hubo: false },
    lecciones:
      'Fixing the mesh tension-free at the pubic tubercle reduces recurrence at the so-called triangle of doom.',
  }),
  entry(4, 2024, '2024-09-05', {
    paciente: { iniciales: 'L.M.C.', edad: 47, sexo: 'F' },
    diagnostico: 'Chronic calculous cholecystitis',
    categoria: 'Laparoscopic cholecystectomy',
    tecnica: 'Laparoscopic',
    rol: 'First assistant',
    duracionMin: 90,
    descripcion:
      'Four trocars. Scleroatrophic gallbladder with omental adhesions that were difficult to dissect. Combined blunt and scissor dissection. Critical view of safety obtained after releasing the adhesions. Clipping of the cystic elements and extraction in an endobag. Hemostasis check and closure.',
    materiales: ['10 mm trocar', 'Endobag', 'Titanium clips'],
    complicaciones: { hubo: false },
    lecciones:
      'The scleroatrophic gallbladder demands patience when dissecting the bed: better to lose ten minutes than to injure the hilar plate.',
  }),
  entry(5, 2024, '2024-11-18', {
    paciente: { iniciales: 'C.B.H.', edad: 58, sexo: 'F' },
    diagnostico: 'Incarcerated umbilical hernia',
    categoria: 'Umbilical hernioplasty',
    tecnica: 'Open',
    rol: 'Lead surgeon',
    duracionMin: 45,
    descripcion:
      'Semilunar infraumbilical incision. Dissection of the sac down to the ring and reduction of viable contents. Closure of the defect with a continuous 2-0 suture and reinforcement with a retrorectus mesh. Skin closure with an intradermal suture.',
    materiales: ['Polypropylene mesh', '2-0 suture', '4-0 suture'],
    complicaciones: { hubo: false },
    lecciones:
      'In umbilical defects larger than 2 cm, retrorectus mesh reinforcement is safer than simple closure.',
  }),

  // ───────────────────────── 2025 (9) ─────────────────────────
  entry(1, 2025, '2025-01-15', {
    paciente: { iniciales: 'A.F.D.', edad: 39, sexo: 'F' },
    diagnostico: 'Symptomatic cholelithiasis',
    categoria: 'Laparoscopic cholecystectomy',
    tecnica: 'Laparoscopic',
    rol: 'Lead surgeon',
    duracionMin: 70,
    descripcion:
      'Pneumoperitoneum and four trocars. Dissection of Calot’s triangle without incident up to the critical view. Clipping and division of the cystic artery and duct. Extraction in an endobag. Hemostasis and routine closure.',
    materiales: ['10 mm trocar', 'Endobag', 'Titanium clips'],
    complicaciones: { hubo: false },
    lecciones:
      'Gentle, steady traction on the infundibulum opens Calot’s triangle better than any aggressive dissection.',
  }),
  entry(2, 2025, '2025-02-11', {
    paciente: { iniciales: 'P.Q.V.', edad: 24, sexo: 'M' },
    diagnostico: 'Acute phlegmonous appendicitis',
    categoria: 'Laparoscopic appendectomy',
    tecnica: 'Laparoscopic',
    rol: 'Lead surgeon',
    duracionMin: 60,
    descripcion:
      'Hasson access. Retrocecal phlegmonous appendix. Mobilization of the cecum and division of the mesoappendix with a harmonic scalpel. Double endoloop at the base and division. Irrigation and hemostasis check. Layered closure.',
    materiales: ['Harmonic scalpel', 'Endoloop', '12 mm trocar'],
    complicaciones: { hubo: false },
    lecciones:
      'The retrocecal appendix requires mobilizing the cecum before attempting ligation: anatomy rules.',
  }),
  entry(3, 2025, '2025-03-04', {
    paciente: { iniciales: 'S.N.O.', edad: 45, sexo: 'F' },
    diagnostico: 'Multinodular goiter with compressive symptoms',
    categoria: 'Total thyroidectomy',
    tecnica: 'Open',
    rol: 'Supervisor',
    duracionMin: 150,
    descripcion:
      'Transverse cervicotomy. Midline opening and separation of the strap muscles. Identification and preservation of the recurrent laryngeal nerve on both sides. Capsular dissection with ligation of the pedicles. Three parathyroids identified and preserved in situ. Meticulous hemostasis, closed-suction drain and layered closure.',
    materiales: ['3-0 suture', 'Closed-suction drain', 'Farabeuf retractor'],
    complicaciones: { hubo: false },
    lecciones:
      'Capsular dissection protects the parathyroids; the recurrent laryngeal nerve is always looked for, never assumed.',
  }),
  entry(4, 2025, '2025-03-25', {
    paciente: { iniciales: 'G.E.U.', edad: 66, sexo: 'M' },
    diagnostico: 'Acute cholecystitis with omental phlegmon',
    categoria: 'Laparoscopic cholecystectomy',
    tecnica: 'Laparoscopic',
    rol: 'Lead surgeon',
    duracionMin: 95,
    descripcion:
      'Firm omental phlegmon over the gallbladder bed making dissection difficult. Adhesiolysis with scissors and blunt dissection. Fundus-down strategy: dissection from the fundus toward the neck until the cystic-hepatic junction was identified. Moderate bleeding from the bed controlled with electrocoagulation and a topical hemostatic. Extraction in an endobag and a drain left in the bed.',
    materiales: ['10 mm trocar', 'Endobag', 'Topical hemostatic', 'Flat drain'],
    complicaciones: {
      hubo: true,
      grado: 'II',
      descripcion: 'Bleeding from the gallbladder bed requiring electrocoagulation and a topical hemostatic.',
    },
    lecciones:
      'In cholecystectomies with a phlegmon, fundus-down dissection reduces the risk of bile duct injury.',
  }),
  entry(5, 2025, '2025-05-20', {
    paciente: { iniciales: 'H.W.Z.', edad: 51, sexo: 'M' },
    diagnostico: 'Direct left inguinal hernia',
    categoria: 'Inguinal hernioplasty',
    tecnica: 'Laparoscopic',
    rol: 'Lead surgeon',
    duracionMin: 85,
    descripcion:
      'TAPP technique. Peritoneal incision and dissection of the preperitoneal space up to the triangle of doom and the triangle of pain. Reduction of the direct sac. Placement of a polypropylene mesh fixed with an absorbable tacker. Peritoneal closure with a continuous suture.',
    materiales: ['Polypropylene mesh 15×10', 'Absorbable tacker', '3-0 suture'],
    complicaciones: { hubo: false },
    lecciones:
      'In TAPP, respecting the triangle of pain avoids meralgia; the mesh must overlap every potential defect by 3 cm.',
  }),
  entry(6, 2025, '2025-06-10', {
    paciente: { iniciales: 'I.X.Y.', edad: 71, sexo: 'F' },
    diagnostico: 'Stage II sigmoid colon neoplasm',
    categoria: 'Colon resection (sigmoidectomy)',
    tecnica: 'Robotic',
    rol: 'Lead surgeon',
    duracionMin: 240,
    descripcion:
      'Docking of four robotic arms. Medial-to-lateral dissection with identification and preservation of the left ureter. Ligation of the inferior mesenteric artery with clips and division. Mobilization of the splenic flexure. Specimen extraction through a suprapubic minilaparotomy and stapled end-to-end colorectal anastomosis. Leak test negative.',
    materiales: ['29 mm circular stapler', 'Linear stapler', 'Titanium clips'],
    complicaciones: { hubo: false },
    lecciones:
      'Robotic medial-to-lateral dissection offers superior ureter exposure; tension-free splenic flexure mobilization is the key to a safe anastomosis.',
  }),
  entry(7, 2025, '2025-06-24', {
    paciente: { iniciales: 'K.L.M.', edad: 63, sexo: 'M' },
    diagnostico: 'Incisional hernia on a midline laparotomy scar',
    categoria: 'Incisional hernia repair with mesh',
    tecnica: 'Open',
    rol: 'Lead surgeon',
    duracionMin: 110,
    descripcion:
      'Reopening of the midline scar. Careful adhesiolysis given the risk of bowel injury. Identification of healthy aponeurotic edges. Mesh placed in a retrorectus sublay position (Rives-Stoppa) and midline closure with a long-loop continuous suture. Two closed-suction drains.',
    materiales: ['Polypropylene mesh 20×15', 'Long-loop suture', 'Closed-suction drain'],
    complicaciones: { hubo: false },
    lecciones:
      'In the Rives-Stoppa technique, dissecting the full retrorectus space out to the semilunar lines is what allows a wide, tension-free mesh.',
  }),
  entry(8, 2025, '2025-08-26', {
    paciente: { iniciales: 'N.O.P.', edad: 57, sexo: 'F' },
    diagnostico: '18 mm sessile polyp of the descending colon',
    categoria: 'Endoscopic colon polypectomy',
    tecnica: 'Endoscopic',
    rol: 'First assistant',
    duracionMin: 35,
    descripcion:
      'Therapeutic colonoscopy. Paris 0-IIa sessile polyp identified. Submucosal lifting with dyed saline. En-bloc resection with a diathermic snare. Bed clipped given the risk of delayed bleeding. Specimen retrieved for pathology.',
    materiales: ['Diathermic snare', 'Endoscopic clips', 'Dyed saline'],
    complicaciones: { hubo: false },
    lecciones:
      'Adequate submucosal lifting is the best predictor of an en-bloc resection without perforation.',
  }),
  entry(9, 2025, '2025-10-14', {
    paciente: { iniciales: 'Q.R.S.', edad: 68, sexo: 'M' },
    diagnostico: 'Hartmann colostomy for deferred reversal',
    categoria: 'Colostomy reversal',
    tecnica: 'Other',
    rol: 'Lead surgeon',
    duracionMin: 130,
    descripcion:
      'Reversal of a Hartmann colostomy. Circumstomal incision and dissection of the colonic stump down to the peritoneum. Moderate adhesiolysis due to obliterated planes. Division of the fibrosed edge and hand-sewn end-to-end anastomosis in two layers. Hemostasis check and closure of the wall in two aponeurotic layers.',
    materiales: ['3-0 suture', '4-0 suture', 'Closed-suction drain'],
    complicaciones: { hubo: false },
    lecciones:
      'In stoma reversal, resecting back to well-vascularized tissue matters more than any chosen suture type.',
  }),
];

/** Returns the seed with the 3 summaries pre-generated by the engine (§14). */
export function buildSeed(): Surgery[] {
  const withFicha = new Set(['semilla-2025-0003', 'semilla-2025-0004', 'semilla-2025-0006']);
  const t = i18n.getFixedT(i18n.language);
  return SEED.map((c) =>
    withFicha.has(c.id) ? { ...c, ficha: generateFicha(c, t) } : { ...c },
  );
}
